/* Review & order: every sheet at a glance, the checks, the price with its
   bulk tiers, then Order my gangsheet (which hands over to the existing
   order flow: freeze, 300 DPI print file, Printees upload page). */

import { formatDims, sheetStats } from '@gsb/core';
import { useStore, setActiveSheet } from '../store/store';
import { SheetThumb } from '../canvas/SheetThumb';
import { Icon } from '../ui/icons';
import { Dialog } from '../ui/primitives';
import { PriceCard, PreflightCard, usePreflight, canOrder } from '../ui/Panels';
import { TransferSeg } from './controls';

export function ReviewDialog({ onClose, onOrder }: { onClose: () => void; onOrder: () => void }) {
  const doc = useStore(s => s.doc);
  const active = useStore(s => s.activeSheet);
  const cfg = useStore(s => s.config)!;
  const pf = usePreflight();
  const ok = canOrder(pf, doc);
  const unit = doc.settings.unit === 'mm' ? 'cm' : doc.settings.unit;
  const all = doc.sheets.map(sheetStats);
  const film = all.reduce((a, s) => a + s.filmMm2, 0), art = all.reduce((a, s) => a + s.artMm2, 0);
  const pct = film ? Math.round((art / film) * 100) : 0;
  const transfers = all.reduce((n, s) => n + s.count, 0);
  const tp = cfg.pricing.transfers[doc.transfer];
  return (
    <Dialog title="Review and order" onClose={onClose} wide className="review-dlg">
      <div className="rv">
        <div className="rv-l">
          <span className="kicker">Review &amp; order</span>
          <h2>{doc.name || 'Your gangsheet'}</h2>
          <p className="small muted rv-sum">
            <span className={'tchip t-' + doc.transfer}>{doc.transfer === 'uv' ? 'UV DTF' : 'DTF'}</span>
            {doc.sheets.length} sheet{doc.sheets.length === 1 ? '' : 's'} · {transfers} transfer{transfers === 1 ? '' : 's'} · {pct}% film used
          </p>
          <ul className="rv-sheets">
            {doc.sheets.map((s, i) => (
              <li key={s.id}>
                <button className={'rv-sheet' + (i === active ? ' is-on' : '')} onClick={() => { setActiveSheet(i); onClose(); }} aria-label={'Show sheet ' + (i + 1) + ' on the canvas'}>
                  <SheetThumb sheet={s} width={128} maxHeight={220} />
                  <b>Sheet {i + 1}</b>
                  <span className="num">{formatDims(s.widthMm, s.lengthMm, unit)}</span>
                  <small>{s.items.length} transfers · {Math.round(all[i].utilisation * 100)}%</small>
                </button>
              </li>
            ))}
          </ul>
          <div className="rv-type">
            <span className="label">Printing</span>
            <TransferSeg size="md" />
            <span className="tiny muted">{tp.blurb}</span>
          </div>
          <div className="rv-next">
            <span className="label">What happens next</span>
            <ol>
              <li>We check your gangsheet and make the 300 DPI print file.</li>
              <li>On printees.co.nz you tell us who’s ordering and choose standard or rush turnaround.</li>
              <li>We confirm the final price before anything is charged, then print it.</li>
            </ol>
          </div>
        </div>
        <div className="rv-r">
          <PreflightCard onEdit={onClose} />
          <PriceCard />
          <div className="rv-cta">
            <button className="btn btn-primary btn-lg btn-block" disabled={!ok.ok} onClick={onOrder}>Order my gangsheet <Icon name="arrowRight" className="ico-arrow" /></button>
            {!ok.ok && ok.why && <p className="tiny rv-why"><Icon name="alert" />{ok.why}</p>}
            <div className="grid2">
              <button className="btn btn-line btn-sm" onClick={onClose}><Icon name="edit" />Keep editing</button>
              <button className="btn btn-line btn-sm" onClick={() => useStore.setState({ preview: true, reviewOpen: false })}><Icon name="eye" />Print preview</button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
